import { useState } from "react";
import { CheckCircle, Clock, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Course } from "@/types/course";

interface CourseEditDialogProps {
  course: Course | null;
  isOpen: boolean;
  onClose: () => void;
  onCourseUpdated: () => void;
}

const CourseEditDialog = ({ course, isOpen, onClose, onCourseUpdated }: CourseEditDialogProps) => {
  const { toast } = useToast();
  const [editForm, setEditForm] = useState({
    title: "",
    description: "",
    price: "",
  });

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditForm(f => ({
      ...f,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSaveEdit = async () => {
    if (!course) return;
    
    // Validate minimum price
    const priceValue = parseFloat(editForm.price);
    if (isNaN(priceValue) || priceValue < 5) {
      toast({
        title: "Invalid Price",
        description: "Minimum price is A$5.00",
        variant: "destructive",
      });
      return;
    }
    
    try {
      const { error } = await supabase
        .from("courses")
        .update({
          title: editForm.title,
          description: editForm.description,
          price: priceValue,
          currency: 'aud' // Ensure AUD currency on updates
        })
        .eq("id", course.id);

      if (error) throw error;

      toast({
        title: "Course Updated",
        description: "Your course has been updated.",
      });
      onClose();
      onCourseUpdated();
    } catch (error) {
      toast({
        title: "Error",
        description: "Unable to update course.",
        variant: "destructive",
      });
    }
  };

  // Update form when course changes
  if (course && (editForm.title !== course.title || editForm.description !== course.description || editForm.price !== course.price?.toString())) {
    setEditForm({
      title: course.title || "",
      description: course.description || "",
      price: course.price?.toString() ?? "",
    });
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Edit Course</DialogTitle>
          <DialogDescription>
            Edit your course details below. All prices are in Australian Dollars (AUD).
          </DialogDescription>
        </DialogHeader>
        {course && (
          <div className="space-y-6 py-4">
            <form className="space-y-4" onSubmit={e => { e.preventDefault(); handleSaveEdit(); }}>
              <div>
                <Label htmlFor="edit-title">Course Title</Label>
                <Input
                  id="edit-title"
                  name="title"
                  value={editForm.title}
                  onChange={handleEditChange}
                />
              </div>
              <div>
                <Label htmlFor="edit-description">Description</Label>
                <Input
                  id="edit-description"
                  name="description"
                  value={editForm.description}
                  onChange={handleEditChange}
                />
              </div>
              <div>
                <Label htmlFor="edit-price">Price (AUD)</Label>
                <div className="flex items-center">
                  <span className="mr-2 text-gray-600">A$</span>
                  <Input
                    id="edit-price"
                    name="price"
                    type="number"
                    value={editForm.price}
                    onChange={handleEditChange}
                    placeholder="Minimum A$5.00"
                  />
                  <DollarSign className="ml-2 text-gray-400" size={18} />
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  All prices are in Australian Dollars (AUD)
                </p>
              </div>
              <div className="mt-6">
                <Label className="text-sm text-gray-500">Course Status</Label>
                <div className="mt-1">
                  {course.status === 'pending' ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      <Clock className="w-3 h-3 mr-1" />
                      Pending Approval
                    </span>
                  ) : course.status === 'rejected' ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      Rejected
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Approved
                    </span>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" type="button" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit">Save Changes</Button>
              </DialogFooter>
            </form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CourseEditDialog;
